import { tryParseMoney } from '@sitzmark/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNotice } from '../../components/notice.js';
import { Badge, Button, Callout, Card, Modal, More, Screen } from '../../components/ui.js';
import { api, errorMessage } from '../../lib/api.js';
import { recordBreadcrumb } from '../../lib/diagnostics.js';
import { money } from '../../lib/money.js';
import {
  cellKey,
  draftsFrom,
  gapsStartOff,
  marginPercent,
  matrixCells,
  parseAxis,
  selectedCount,
} from './product-matrix.js';
import type { MatrixCell, MatrixInput } from './product-matrix.js';
import { BrandPicker } from './brand-picker.js';
import { Picker } from './style-settings.js';
import './products.css';

/**
 * Grid entry (SCOPE §5.2) — "enter a style once, define colors (rows) × sizes
 * (columns) … variants generate automatically. This mirrors how buyers think
 * and is the client's explicitly requested format."
 *
 * The speed is in the axes being *typed as lists*: `Black, Red, Storm Blue` and
 * `S, M, L, XL` is sixteen variants in two fields. The grid below them is for
 * the exception — the colourway that does not come in XS — which is unticked
 * rather than built up cell by cell (§2.2-5).
 *
 * Single-SKU entry is the same screen with both axes left blank. There is no
 * second mode to choose between, because "does this thing have sizes?" is a
 * question the buyer answers by typing sizes or not (§2.2-2).
 */

type Detail = Awaited<ReturnType<typeof api.products.get.query>>;

interface Defaults {
  cost: string;
  msrp: string;
  retail: string;
  target: string;
  min: string;
}

const EMPTY_DEFAULTS: Defaults = { cost: '', msrp: '', retail: '', target: '', min: '' };

function toVariantDefaults(defaults: Defaults) {
  const msrp = tryParseMoney(defaults.msrp);
  return {
    costCents: tryParseMoney(defaults.cost) ?? 0,
    msrpCents: msrp,
    // A style with no retail price typed is priced at MSRP, exactly as the
    // import wizard does it (§5.1-5) — the common case is confirmation.
    retailCents: tryParseMoney(defaults.retail) ?? msrp ?? 0,
    targetQty: parseCount(defaults.target),
    minQty: parseCount(defaults.min),
  };
}

function parseCount(raw: string): number | null {
  const digits = raw.replace(/\D/g, '');
  return digits === '' ? null : Number.parseInt(digits, 10);
}

// ---------------------------------------------------------------------------

/**
 * Creating a style is a workspace, not a question — so it is a screen.
 *
 * It began as a modal and the grid was unusable in one: `.modal__body` caps at
 * 60vh, and a scroll container is the only flex child that may shrink below its
 * content, so the grid — the whole point of the screen — collapsed to a sliver
 * under six fields of taxonomy. The CSS bug is fixed either way, but the real
 * error was the container. `Modal` is documented as being for a question or a
 * table you are reading; this is neither. As a screen the grid gets the room it
 * needs, the anatomy matches every other screen (§2.2-4), and there is one
 * scrollbar — the page's.
 */
export function NewStyleScreen({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (productId: string) => void;
}) {
  const notify = useNotice();
  const taxonomy = useQuery({
    queryKey: ['products', 'taxonomy'],
    queryFn: () => api.products.taxonomy.query(),
    staleTime: 300_000,
  });

  const [styleNumber, setStyleNumber] = useState('');
  const [name, setName] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [brandId, setBrandId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [season, setSeason] = useState('');
  const [trackInventory, setTrackInventory] = useState(true);

  const grid = useGrid(new Set<string>());
  const [defaults, setDefaults] = useState(EMPTY_DEFAULTS);

  const create = useMutation({
    mutationFn: () =>
      api.products.create.mutate({
        styleNumber: styleNumber.trim(),
        name: name.trim(),
        vendorId,
        brandId: brandId === '' ? null : brandId,
        departmentId,
        categoryId: categoryId === '' ? null : categoryId,
        season: season.trim() === '' ? null : season.trim(),
        trackInventory,
        variants: draftsFrom(grid.cells, toVariantDefaults(defaults)),
      }),
    onSuccess: (result) => {
      recordBreadcrumb('products', `created a model with ${result.createdVariants} variants`);
      notify({
        title: `${name.trim()} created — ${result.createdVariants} ${
          result.createdVariants === 1 ? 'size' : 'sizes'
        }`,
      });
      onCreated(result.productId);
    },
  });

  const count = selectedCount(grid.cells);
  const problem =
    styleNumber.trim() === ''
      ? 'Give it a model number'
      : name.trim() === ''
        ? 'Give it a name'
        : vendorId === ''
          ? 'Pick a vendor'
          : departmentId === ''
            ? 'Pick a category'
            : count === 0
              ? 'Nothing is checked in the grid'
              : undefined;

  return (
    <Screen
      title="New model"
      subtitle="Enter it once. The colors and sizes below become the variants."
      secondaryActions={
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      }
      primaryAction={
        <Button
          variant="primary"
          size="large"
          busy={create.isPending}
          disabledReason={problem}
          onClick={() => create.mutate()}
        >
          Create {count} variant{count === 1 ? '' : 's'}
        </Button>
      }
    >
      <Card title="The model">
        <div className="style-form">
          <Field label="Model number" value={styleNumber} onChange={setStyleNumber} mono />
          <Field label="Name" value={name} onChange={setName} />

          <Picker
            label="Vendor"
            searchable
            value={vendorId}
            onChange={setVendorId}
            options={(taxonomy.data?.vendors ?? []).map((vendor) => ({
              value: vendor.id,
              label: vendor.name,
            }))}
          />
          <BrandPicker
            value={brandId}
            onChange={setBrandId}
            optional
            brands={taxonomy.data?.brands ?? []}
            vendorName={
              taxonomy.data?.vendors.find((vendor) => vendor.id === vendorId)?.name ?? null
            }
          />
          {/* Filed at any level, read as a path; gender from the store's list (decision 214). */}
          <Picker
            label="Category"
            searchable
            value={departmentId}
            onChange={setDepartmentId}
            options={(taxonomy.data?.departments ?? []).map((category) => ({
              value: category.id,
              label: category.path,
            }))}
          />
          {/* A store whose categories say who it is for keeps no genders (decision 224). */}
          {(taxonomy.data?.genders ?? []).length > 0 ? (
            <Picker
              label="Gender"
              value={categoryId}
              onChange={setCategoryId}
              optional
              options={(taxonomy.data?.genders ?? []).map((gender) => ({
                value: gender.id,
                label: `${gender.name}${gender.group === null ? '' : ` · ${gender.group}`}`,
              }))}
            />
          ) : null}
          <Field label="Season" value={season} onChange={setSeason} placeholder="FA26" />
        </div>

        <label className="toggle">
          <input
            type="checkbox"
            checked={!trackInventory}
            onChange={(event) => setTrackInventory(!event.target.checked)}
          />
          <span>This is a service, not stock — priced and reported, never counted</span>
        </label>
      </Card>

      <Card title="Colors &amp; sizes">
        <GridEditor grid={grid} />
      </Card>

      <Card title="Cost &amp; price">
        <DefaultsEditor defaults={defaults} onChange={setDefaults} />
      </Card>

      {create.isError ? <Callout tone="danger">{errorMessage(create.error)}</Callout> : null}
    </Screen>
  );
}

// ---------------------------------------------------------------------------

export function AddVariantsDialog({
  productId,
  existing,
  onClose,
  onAdded,
}: {
  productId: string;
  existing: readonly {
    id: string;
    active: boolean;
    colorName: string | null;
    sizeLabel: string | null;
    altSize: string | null;
    retailCents: number;
    costCents: number;
    msrpCents: number | null;
  }[];
  onClose: () => void;
  onAdded: (detail: Detail) => void;
}) {
  const notify = useNotice();
  const existingKeys = useMemo(
    () =>
      new Set(
        existing.map((variant) => cellKey(variant.colorName, variant.sizeLabel, variant.altSize)),
      ),
    [existing],
  );

  const startsOff = useMemo(() => gapsStartOff(existing), [existing]);
  const grid = useGrid(
    existingKeys,
    {
      // Pre-filled with what the style already has, so adding one size to four
      // colours is a single keystroke rather than retyping the colourways.
      colors: [...new Set(existing.map((variant) => variant.colorName).filter(isPresent))].join(
        ', ',
      ),
      sizes: [...new Set(existing.map((variant) => variant.sizeLabel).filter(isPresent))].join(
        ', ',
      ),
      lengths: [...new Set(existing.map((variant) => variant.altSize).filter(isPresent))].join(
        ', ',
      ),
    },
    startsOff,
  );

  /*
   * Taking sizes off (decision 208). "In sugar bush red was a test row … when
   * i go to add colors and sizes i can remove the red but it doesn't let me
   * save it because it says no new variants" (client, 2026-10-15). A size the
   * style has can be unticked in the grid, or dropped by taking its color,
   * size or length out of the boxes above; either way it is listed before
   * Save, and Save says how many go.
   */
  const [unticked, setUnticked] = useState<ReadonlySet<string>>(new Set());
  const live = existing.filter((variant) => variant.active);
  const onGrid = new Set(grid.cells.map((cell) => cell.key));
  const keyOf = (variant: (typeof existing)[number]) =>
    cellKey(variant.colorName, variant.sizeLabel, variant.altSize);
  const goingOff = live.filter(
    (variant) => unticked.has(keyOf(variant)) || !onGrid.has(keyOf(variant)),
  );
  const inactiveKeys = new Set(
    existing.filter((variant) => !variant.active).map((variant) => keyOf(variant)),
  );

  const [defaults, setDefaults] = useState<Defaults>(() => {
    // What the rest of the style already costs and sells at is the right guess —
    // cost included: left blank, a new length came in at $0.00 (2026-10-02).
    const like = existing[0];
    return {
      ...EMPTY_DEFAULTS,
      cost: like === undefined ? '' : (like.costCents / 100).toFixed(2),
      msrp: like === undefined || like.msrpCents === null ? '' : (like.msrpCents / 100).toFixed(2),
      retail: like === undefined ? '' : (like.retailCents / 100).toFixed(2),
    };
  });

  const toAdd = draftsFrom(grid.cells, toVariantDefaults(defaults));
  const save = useMutation({
    mutationFn: () =>
      api.products.changeVariants.mutate({
        productId,
        add: toAdd,
        remove: goingOff.map((variant) => variant.id),
      }),
    onSuccess: (result) => {
      recordBreadcrumb('products', 'changed the colors and sizes of a model');
      const parts = [
        result.added > 0 ? `added ${result.added}` : null,
        result.deleted.length > 0 ? `took off ${result.deleted.length}` : null,
        result.deactivated.length > 0 ? `made ${result.deactivated.length} inactive` : null,
      ].filter((part): part is string => part !== null);
      const sentence = parts.join(', ');
      notify({
        title: `${result.product.product.name} — ${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}`,
        ...(result.deactivated.length > 0
          ? {
              detail: `${result.deactivated.map((entry) => entry.sku).join(', ')} had sales, stock or orders, so they keep their history as inactive sizes.`,
            }
          : {}),
      });
      onAdded(result.product);
    },
  });

  const count = selectedCount(grid.cells);
  const nothing = count === 0 && goingOff.length === 0;

  return (
    <Modal
      open
      wide
      title="Colors &amp; sizes"
      description="Check what to add. Untick a size the model has — or take its color or size out of the boxes — to take it off. A size's own Mfr #, barcode and bin are on the size: close this and press its box."
      onClose={onClose}
      secondaryAction={
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      }
      primaryAction={
        <Button
          variant="primary"
          busy={save.isPending}
          disabledReason={nothing ? 'Nothing to add or take off' : undefined}
          onClick={() => save.mutate()}
        >
          {goingOff.length === 0
            ? `Add ${count} variant${count === 1 ? '' : 's'}`
            : count === 0
              ? `Take off ${goingOff.length}`
              : `Add ${count}, take off ${goingOff.length}`}
        </Button>
      }
    >
      <GridEditor
        grid={grid}
        have={{
          unticked,
          inactive: inactiveKeys,
          toggle: (key) =>
            setUnticked((current) => {
              const next = new Set(current);
              if (next.has(key)) next.delete(key);
              else next.add(key);
              return next;
            }),
        }}
      />
      {goingOff.length > 0 ? (
        <Callout tone="warning" title={`Taking off ${goingOff.length}`}>
          <p>
            {goingOff
              .map(
                (variant) =>
                  [variant.colorName, variant.sizeLabel, variant.altSize]
                    .filter((part) => part !== null && part !== '')
                    .join(' · ') || 'One size',
              )
              .join(', ')}
          </p>
          <p className="text-secondary">
            A size nothing ever happened to is deleted. One that was sold, received, counted or
            ordered is made inactive instead, and keeps its history.
          </p>
        </Callout>
      ) : null}
      {count > 0 ? <DefaultsEditor defaults={defaults} onChange={setDefaults} /> : null}
      {save.isError ? <Callout tone="danger">{errorMessage(save.error)}</Callout> : null}
    </Modal>
  );
}

function isPresent(value: string | null): value is string {
  return value !== null && value.trim() !== '';
}

// ---------------------------------------------------------------------------

interface Grid {
  colorsRaw: string;
  sizesRaw: string;
  lengthsRaw: string;
  setColorsRaw: (value: string) => void;
  setSizesRaw: (value: string) => void;
  setLengthsRaw: (value: string) => void;
  cells: MatrixCell[];
  toggle: (key: string) => void;
  toggleMany: (keys: readonly string[]) => void;
  colors: string[];
  sizes: string[];
  lengths: string[];
}

function useGrid(
  existing: ReadonlySet<string>,
  initial: { colors: string; sizes: string; lengths?: string } = { colors: '', sizes: '' },
  startsOff?: MatrixInput['startsOff'],
): Grid {
  const [colorsRaw, setColorsRaw] = useState(initial.colors);
  const [sizesRaw, setSizesRaw] = useState(initial.sizes);
  const [lengthsRaw, setLengthsRaw] = useState(initial.lengths ?? '');
  const [deselected, setDeselected] = useState<ReadonlySet<string>>(new Set());

  const colors = useMemo(() => parseAxis(colorsRaw), [colorsRaw]);
  const sizes = useMemo(() => parseAxis(sizesRaw), [sizesRaw]);
  const lengths = useMemo(() => parseAxis(lengthsRaw), [lengthsRaw]);
  const cells = useMemo(
    () =>
      matrixCells({
        colors,
        sizes,
        lengths,
        deselected,
        existing,
        ...(startsOff === undefined ? {} : { startsOff }),
      }),
    [colors, sizes, lengths, deselected, existing, startsOff],
  );

  const toggle = (key: string) =>
    setDeselected((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  /**
   * Turn a whole row or column on or off.
   *
   * "This style does not come in XS" is one press, not eleven. Off when any of
   * them are currently on, which is what a spreadsheet does and what a hand
   * expects.
   */
  const toggleMany = (keys: readonly string[]) =>
    setDeselected((current) => {
      const byKey = new Map(cells.map((cell) => [cell.key, cell]));
      const selectable = keys.filter((key) => !existing.has(key) && byKey.has(key));
      const offAtStart = (key: string) => {
        const cell = byKey.get(key)!;
        return startsOff?.({ color: cell.color, size: cell.size, length: cell.length }) === true;
      };
      const isOn = (key: string) => (offAtStart(key) ? current.has(key) : !current.has(key));
      const turnOn = !selectable.some(isOn);
      const next = new Set(current);
      for (const key of selectable) {
        // Pressed away from its start exactly when its start is not where it is going.
        if (offAtStart(key) === turnOn) next.add(key);
        else next.delete(key);
      }
      return next;
    });

  return {
    colorsRaw,
    sizesRaw,
    lengthsRaw,
    setColorsRaw,
    setSizesRaw,
    setLengthsRaw,
    cells,
    toggle,
    toggleMany,
    colors,
    sizes,
    lengths,
  };
}

/**
 * The sizes a style already has, in the Colors & sizes dialog: each a tick to
 * keep, untick to take off (decision 208). Absent when a style is being made.
 */
interface HaveControls {
  unticked: ReadonlySet<string>;
  inactive: ReadonlySet<string>;
  toggle: (key: string) => void;
}

function GridEditor({ grid, have }: { grid: Grid; have?: HaveControls }) {
  const columns = grid.sizes.length === 0 ? [null] : grid.sizes;
  const rows = grid.colors.length === 0 ? [null] : grid.colors;
  const blocks = grid.lengths.length === 0 ? [null] : grid.lengths;
  const byKey = new Map(grid.cells.map((cell) => [cell.key, cell]));

  return (
    <>
      <div className="style-form">
        <Field
          label="Colors"
          value={grid.colorsRaw}
          onChange={grid.setColorsRaw}
          placeholder="Black, Storm Blue, Red"
          hint="Separated by commas. Leave blank if it has no colors."
        />
        <Field
          label="Sizes"
          value={grid.sizesRaw}
          onChange={grid.setSizesRaw}
          placeholder="S, M, L, XL"
          hint="Separated by commas. Leave blank if it has no sizes."
        />
        {/*
          The third axis (2026-10-02): "in some cases they will have a length
          dimension — where is that supposed to be added?" Here, with the
          colors and sizes: each length is a grid of its own.
        */}
        <Field
          label="Lengths"
          value={grid.lengthsRaw}
          onChange={grid.setLengthsRaw}
          placeholder="Short, Regular, Long"
          hint="Only for a model that comes in lengths. Leave blank if it does not — most do not."
        />
      </div>

      {/*
        Said plainly, because it is what the primary button will do (§2.2-6).
        A grid of forty ticks is not something anyone counts by eye.
      */}
      <p className="text-secondary">
        {selectedCount(grid.cells)} of {grid.cells.filter((cell) => !cell.exists).length} checked.
        Tap a color or a size heading to turn its whole row or column on and off.
      </p>

      {blocks.map((length) => (
        <div className="table-scroll" key={length ?? 'one'}>
          <table className="table matrix">
            {length === null ? null : <caption className="matrix__caption">{length}</caption>}
            <thead>
              <tr>
                <th />
                {columns.map((size, index) => (
                  <th key={size ?? `size-${index}`} scope="col">
                    {/* "It does not come in XS" is one press, not eleven. */}
                    <button
                      type="button"
                      className="matrix__heading"
                      onClick={() =>
                        grid.toggleMany(rows.map((color) => cellKey(color, size, length)))
                      }
                    >
                      {size ?? 'One size'}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((color, rowIndex) => (
                <tr key={color ?? `color-${rowIndex}`}>
                  <th scope="row" className="matrix__row-head">
                    <button
                      type="button"
                      className="matrix__heading"
                      onClick={() =>
                        grid.toggleMany(columns.map((size) => cellKey(color, size, length)))
                      }
                    >
                      {color ?? 'One color'}
                    </button>
                  </th>
                  {columns.map((size, index) => {
                    const cell = byKey.get(cellKey(color, size, length));
                    if (cell === undefined) return <td key={index} />;

                    return (
                      <td key={cell.key} className="matrix__cell">
                        {/* Already on the style: shown so the buyer can see it is
                          there, but not a control, because it cannot act
                          (§2.2-13). */}
                        {cell.exists && have !== undefined && !have.inactive.has(cell.key) ? (
                          <label className="matrix__toggle matrix__toggle--have">
                            <input
                              type="checkbox"
                              checked={!have.unticked.has(cell.key)}
                              aria-label={`Keep ${[color ?? 'One color', size ?? 'one size', length]
                                .filter(Boolean)
                                .join(' ')}`}
                              onChange={() => have.toggle(cell.key)}
                            />
                            <span>Have it</span>
                          </label>
                        ) : cell.exists ? (
                          <Badge tone="neutral">
                            {have?.inactive.has(cell.key) === true ? 'Inactive' : 'Have it'}
                          </Badge>
                        ) : (
                          <label className="matrix__toggle">
                            <input
                              type="checkbox"
                              checked={cell.selected}
                              aria-label={[color ?? 'One color', size ?? 'one size', length]
                                .filter(Boolean)
                                .join(' ')}
                              onChange={() => grid.toggle(cell.key)}
                            />
                          </label>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </>
  );
}

function DefaultsEditor({
  defaults,
  onChange,
}: {
  defaults: Defaults;
  onChange: (defaults: Defaults) => void;
}) {
  const set = (key: keyof Defaults) => (value: string) => onChange({ ...defaults, [key]: value });

  const parsed = toVariantDefaults(defaults);
  const margin = marginPercent(parsed.costCents, parsed.retailCents);

  return (
    <>
      <div className="style-form">
        <Field label="Cost each" value={defaults.cost} onChange={set('cost')} placeholder="0.00" />
        <Field label="MSRP" value={defaults.msrp} onChange={set('msrp')} placeholder="0.00" />
        <Field
          label="Retail"
          value={defaults.retail}
          onChange={set('retail')}
          placeholder="MSRP"
          hint="Blank prices it at MSRP."
        />
      </div>

      {/* The number actually being decided, updating as it is typed (§2.2-10). */}
      {margin !== null ? (
        <p className={`text-secondary${margin < 0 ? ' margin--negative' : ''}`}>
          {money(parsed.retailCents)} retail on {money(parsed.costCents)} cost is{' '}
          {margin.toFixed(0)}% margin.
        </p>
      ) : null}

      <More label="Stock targets">
        <div className="style-form">
          <Field
            label="Target quantity"
            value={defaults.target}
            onChange={set('target')}
            hint="What a full rack looks like. Open to buy and a reorder work up to this."
          />
          <Field
            label="Minimum"
            value={defaults.min}
            onChange={set('min')}
            hint="When on hand plus on order falls below this, the size shows in Reports → Reorder."
          />
        </div>
      </More>
    </>
  );
}

// ---------------------------------------------------------------------------

function Field({
  label,
  value,
  onChange,
  placeholder,
  hint,
  mono = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  hint?: string;
  mono?: boolean;
}) {
  const id = `style-${label.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className={`input${mono ? ' code' : ''}`}
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(event) => onChange(event.target.value)}
      />
      {hint !== undefined ? <p className="field__hint">{hint}</p> : null}
    </div>
  );
}
